<script setup>
import { ref, computed, inject } from 'vue'
import VBaseInput from "../Inputs/VBaseInput.vue";
import { useNotification } from "@kyvg/vue3-notification";
import VLoadingIcon from "./VLoadingIcon.vue";
import { useI18n } from "vue-i18n";

const route = inject('route');

const { locale } = useI18n();


const { notify } = useNotification();

const activeAccordion = ref(null)
const email = ref("")
const emailSend = ref(false)
const loading = ref(false)

const validateEmail = computed(() => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(email.value).toLowerCase())
})

const linkToPrivacy = route('pages.privacy-policy');

async function sendEmail() {
    if (validateEmail.value) {
        try {
            loading.value = true;

            const request = await axios.post(route('reminder.send'), {
                email: email.value,
            })

            const { data } = request;

            if (data.success) {
                email.value = ""
                emailSend.value = true
            } else {
                notify({
                    title: "Error",
                    text: "Something went wrong. ",
                    type: "error",
                });
            }
        } catch (error) {
            notify({
                title: "Error",
                text: "Something went wrong. ",
                type: "error",
            });
        } finally {
            loading.value = false;
        }
    }
}
</script>

<template>
    <div id="accordion-collapse" data-accordion="collapse" v-auto-animate>
        <h2 id="accordion-collapse-heading-1">
            <button type="button" @click="activeAccordion = activeAccordion === 1 ? null : 1"
                class="flex items-center justify-between w-full py-8 px-5 font-bold text-lg text-left hover:opacity-90 bg-bi-dark-green text-white"
                data-accordion-target="#accordion-collapse-body-1" aria-expanded="true"
                aria-controls="accordion-collapse-body-1">
                <span>{{ $t('accordion.title.1') }}</span>
                <svg v-if="activeAccordion === 1" data-accordion-icon="" class="ml-4 w-6 h-6 rotate-180 shrink-0"
                    fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"></path>
                </svg>
                <svg v-else data-accordion-icon="" class="ml-4 w-6 h-6 shrink-0" fill="currentColor" viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"></path>
                </svg>
            </button>
        </h2>
        <div id="accordion-collapse-body-1" ref="accordion1" class="bg-bi-dark-green text-text-light mb-[1px]"
            aria-labelledby="accordion-collapse-heading-1" v-if="activeAccordion === 1" v-auto-animate>
            <div class="px-5 pt-2 pb-5" v-if="!emailSend">
                <p class="text-base font-medium">
                    {{ $t('accordion.enter.email') }}
                </p>

                <v-base-input id="email" type="email" placeholder="example@gmail.com" theme="dark" class="mb-1"
                    v-model="email"></v-base-input>

                <p v-html="$t('accordion.terms', { link: route('pages.term-of-use'), privacy: linkToPrivacy })"
                    class="text-sm"></p>

                <div class="flex items-center justify-center mt-8">
                    <button type="button" @click="sendEmail"
                        :class="validateEmail ? 'bg-bi-accent-green text-bi-dark-green' : 'bg-bi-gray-50 text-white pointer-events-none'"
                        class="inline-flex font-bi-text-medium items-center rounded-md border border-transparent px-4 py-2 text-base font-medium shadow-sm hover:opacity-90 focus:outline-none">
                        <v-loading-icon width="24" height="24" class="mr-2" v-if="loading"></v-loading-icon>
                        {{ $t('button.submit') }}
                    </button>
                </div>
            </div>
            <div class="px-5 pt-2 pb-5" v-else>
                <p class="text-lg font-medium">{{ $t('accordion.email.send') }}</p>
            </div>
        </div>
        <h2 id="accordion-collapse-heading-2">
            <button type="button" @click="activeAccordion = activeAccordion === 2 ? null : 2"
                class="flex items-center justify-between w-full py-8 px-5 font-bold text-lg text-left hover:opacity-90 bg-bi-dark-green text-white"
                data-accordion-target="#accordion-collapse-body-2" aria-expanded="false"
                aria-controls="accordion-collapse-body-2">
                <span>{{ $t('accordion.title.2') }}</span>
                <svg v-if="activeAccordion === 2" data-accordion-icon="" class="ml-4 w-6 h-6 rotate-180 shrink-0"
                    fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"></path>
                </svg>
                <svg v-else data-accordion-icon="" class="ml-4 w-6 h-6 shrink-0" fill="currentColor" viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"></path>
                </svg>
            </button>
        </h2>
        <div id="accordion-collapse-body-2" ref="accordion2" class="bg-bi-dark-green text-text-light mb-[1px]"
            aria-labelledby="accordion-collapse-heading-2" v-if="activeAccordion === 2">
            <div class="px-5 pt-2 pb-5">
                <p class="text-base font-medium">
                    {{ $t('accordion.enter.visit') }}
                </p>

                <div class="flex flex-wrap items-start gap-4 sm:gap-8 justify-center my-8">
                    <div class="max-w-[100px]">
                        <a href="https://support.apple.com/en-ca/guide/calendar/icl1023/11.0/mac/12.0"
                            rel="noopener noreferrer nofollow" target="_blank"
                            class="flex items-center justify-center aspect-square w-[89px] bg-bi-light-gray rounded-md mx-auto mb-3 hover:border-2 border-bi-accent-green hover:shadow-lg transition-all duration-100 ease-linear">
                            <img src="../../images/calendar-ios.png" alt="iOS Calendar" width="64" height="64" />
                        </a>
                        <span v-html="$t('home.calendar.ios')"
                            class="block text-sm text-center text-white font-normal"></span>
                    </div>

                    <div class="max-w-[100px]">
                        <a href="https://support.google.com/calendar/answer/37118?hl=en&co=GENIE.Platform%3DDesktop&oco=1#"
                            rel="noopener noreferrer nofollow" target="_blank"
                            class="flex items-center justify-center aspect-square w-[89px] bg-bi-light-gray rounded-md mx-auto mb-3  hover:border-2 border-bi-accent-green hover:shadow-lg transition-all duration-100 ease-linear">
                            <img src="../../images/calendar-google.png" alt="Google Calendar" width="64" height="64" />
                        </a>
                        <span v-html="$t('home.calendar.google')"
                            class="block text-sm text-center text-white font-normal"></span>
                    </div>

                    <div class="max-w-[100px]">
                        <a href="https://support.microsoft.com/en-us/office/import-calendars-into-outlook-8e8364e1-400e-4c0f-a573-fe76b5a2d379"
                            rel="noopener noreferrer nofollow" target="_blank"
                            class="flex items-center justify-center aspect-square w-[89px] bg-bi-light-gray rounded-md mx-auto mb-3  hover:border-2 border-bi-accent-green hover:shadow-lg transition-all duration-100 ease-linear">
                            <img src="../../images/calendar-outlook.png" alt="Outlook Calendar" width="64" height="64" />
                        </a>
                        <span v-html="$t('home.calendar.outlook')"
                            class="block text-sm text-center text-white font-normal"></span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
h2+h2 {
    margin-top: 1px;
}
</style>
